import React, { useState, useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'

import { List } from 'immutable'

import '@aytm/lib/rfd-ui/styles/basic/inline-search.scss'
import useOuterClickListener from '@aytm/lib/rfd-ui/lib/outer-click-listener'

const CLASS = 'rfd-ui-inline-search'

const toText = source =>
  source ? String(source).replace(/&amp;/gi, '&').replace(/&lt;/gi, '<').replace(/&gt;/gi, '>') : ''

const getOptionText = option => {
  const render = option.get('render')
  const text = 'function' === typeof render ? render() : render
  return toText(text)
}

const InlineSearch = props => {
  const listRef = useRef()
  const containerRef = useRef()
  const editing = useRef()
  const timer = useRef(0)

  const propsValue = useMemo(() => props.children || props.value || '', [props.children, props.value])

  const [value, setValue] = useState(propsValue)
  const [showList, setShowList] = useState(false)
  const [index, setIndex] = useState(-1)

  const anchors = props.anchors || InlineSearch.defaultProps.anchors

  const getOptions = () => {
    if (props.fetchOptions) {
      const list = props.fetchOptions().map(option => {
        return { option, text: getOptionText(option) }
      })
      return editing.current ? list.filter(v => v.text.toLowerCase().includes((value || '').toLowerCase())) : list
    } else return List()
  }

  const inputChange = event => {
    const newValue = event.target.value
    editing.current = true
    setValue(newValue)
    setIndex(-1)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      if (getOptions().size > 0) setShowList(true)
      clearTimeout(timer.current)
    }, [props.timeout])
  }

  const closeList = option => () => {
    setShowList(false)
    setIndex(-1)
    if (props.autoClear) setValue('')
    if (option) {
      setValue(getOptionText(option))
      editing.current = false
      if (props.onSelect) props.onSelect({ option })
    } else setValue(propsValue)
  }

  const stepList = to => {
    if (index >= 0 && listRef.current) {
      const rect = listRef.current.getBoundingClientRect()
      const scrollTop = listRef.current.scrollTop
      const element = listRef.current.children[to]
      if (element.offsetTop < scrollTop) listRef.current.scrollTop -= element.clientHeight
      else if (element.offsetTop + element.clientHeight > scrollTop + rect.height)
        listRef.current.scrollTop += element.clientHeight
    }
    setIndex(to)
  }

  const inputKeyDown = event => {
    if (showList) {
      const options = getOptions()
      const length = options.size
      if (length > 0) {
        if (['ArrowDown', 'ArrowRight'].includes(event.key) && index < length - 1) {
          event.preventDefault()
          stepList(index + 1)
        } else if (['ArrowUp', 'ArrowLeft'].includes(event.key) && index > 0) {
          event.preventDefault()
          stepList(index - 1)
        } else if (['Enter'].includes(event.key) && index >= 0 && index < length) {
          event.preventDefault()
          closeList(options.get(index).option)()
        } else if (['Escape'].includes(event.key)) {
          event.preventDefault()
          setShowList(false)
          setValue(propsValue)
        }
      }
    }
  }

  const renderList = () => {
    if (!showList) return null
    const options = getOptions().map((v, i) => {
      const className = i === index ? `${CLASS}-list-option-selected` : ''
      return (
        <div key={i} className={`${CLASS}-list-option ${className}`} onClick={closeList(v.option)}>
          {v.text}
        </div>
      )
    })
    if (options.size) {
      const container = containerRef.current
      if (container) {
        const rect = container.getBoundingClientRect()
        const style = {
          top: props.topMargin || `${rect.height}px`
        }
        if (anchors.includes('left')) style.left = '0px'
        if (anchors.includes('right')) style.right = '0px'
        const listClassName = props.wrap ? `${CLASS}-list ${CLASS}-list-wrap` : `${CLASS}-list`
        return (
          <div className={listClassName} style={style} ref={listRef}>
            {options}
          </div>
        )
      }
    }
    return null
  }

  const iconClick = event => {
    event.stopPropagation()
    editing.current = false
    setShowList(!showList)
  }

  const inputBlur = () => {
    if (!showList) {
      clearTimeout(timer.current)
      setValue(propsValue)
    }
  }

  useOuterClickListener({
    ref: listRef,
    listen: showList,
    onClick: () => {
      setShowList(false)
      setValue(propsValue)
    }
  })

  useEffect(() => {
    const newValue = propsValue
    if (!editing.current && value !== newValue) setValue(newValue)
  }, [propsValue, value])

  useEffect(() => {
    const list = listRef.current
    const onWheel = event => event.stopPropagation()
    if (showList && list) {
      list.addEventListener('wheel', onWheel, { passive: false })
      return () => list.removeEventListener('wheel', onWheel)
    } else return () => {}
  }, [showList])

  const iconClass = props.selector
    ? showList
      ? `icon-aytmfd-chevron-bracket ${CLASS}-icon ${CLASS}-icon-selector ${CLASS}-icon-up`
      : `icon-aytmfd-chevron-bracket ${CLASS}-icon ${CLASS}-icon-selector ${CLASS}-icon-down`
    : `pi pi-search ${CLASS}-icon`

  const text = toText(value)

  const input = props.readOnly ? (
    <div
      className={`${CLASS}-input ${CLASS}-input-read-only`}
      tabIndex={1}
      onClick={iconClick}
      onKeyDown={inputKeyDown}
    >
      {text}
    </div>
  ) : props.multiline ? (
    <textarea
      className={`${CLASS}-input`}
      placeholder={props.placeholder}
      value={text}
      onBlur={inputBlur}
      onChange={inputChange}
      onKeyDown={inputKeyDown}
    />
  ) : (
    <input
      className={`${CLASS}-input`}
      type={'text'}
      placeholder={props.placeholder}
      value={text}
      onBlur={inputBlur}
      onChange={inputChange}
      onKeyDown={inputKeyDown}
    />
  )

  const label = props.label ? (
    React.isValidElement(props.label) ? (
      props.label
    ) : (
      <div className={`${CLASS}-label`} dangerouslySetInnerHTML={{ __html: props.label }} />
    )
  ) : null

  return (
    <span className={`${CLASS} ${props.className}`} ref={containerRef}>
      {label}
      {input}
      <i className={iconClass} onClick={iconClick} />
      {renderList()}
    </span>
  )
}

InlineSearch.propTypes = {
  className: PropTypes.string,
  placeholder: PropTypes.string,
  fetchOptions: PropTypes.func,
  timeout: PropTypes.number,
  autoClear: PropTypes.any,
  children: PropTypes.any,
  value: PropTypes.any,
  selector: PropTypes.any,
  multiline: PropTypes.any,
  label: PropTypes.any,
  readOnly: PropTypes.any,
  anchors: PropTypes.string,
  wrap: PropTypes.any,
  topMargin: PropTypes.string,
  onSelect: PropTypes.func
}

InlineSearch.defaultProps = {
  autoClear: false,
  timeout: 1000,
  selector: false,
  multiline: false,
  readOnly: false,
  anchors: 'left right',
  wrap: true,
  value: '',
  children: ''
}

export default InlineSearch
